import React, { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Tooltip } from '../ui/Tooltip';
import { getPhaseHelpLines } from './cycleTimePhaseHelp';

const CHEVRON_PX = 14;

/** @param {number|null|undefined} m */
export function formatPhaseDuration(m) {
  if (m == null || Number.isNaN(Number(m))) return '—';
  const mins = Number(m);
  if (mins >= 1440) {
    const d = mins / 1440;
    const rounded = d >= 10 ? Math.round(d) : Math.round(d * 10) / 10;
    return `${rounded} Day${rounded === 1 ? '' : 's'}`;
  }
  if (mins >= 60) {
    const h = mins / 60;
    const rounded = h >= 10 ? Math.round(h) : Math.round(h * 10) / 10;
    return `${rounded} Hr${rounded === 1 ? '' : 's'}`;
  }
  return `${Math.round(mins)} Min`;
}

/**
 * @param {object|null|undefined} summary - cycleTime.summary from API (mean coding/review + cadence)
 * @returns {{ key: string, label: string, minutes: number|null }[]}
 */
export function buildPhasesFromSummary(summary) {
  if (!summary) return [];
  return [
    { key: 'coding', label: 'Coding', minutes: summary.meanCodingMinutes ?? null },
    { key: 'pickup', label: 'Pickup', minutes: summary.meanPickupMinutes ?? null },
    { key: 'review', label: 'Review', minutes: summary.meanReviewMinutes ?? null },
    { key: 'deploy', label: 'Deploy', minutes: summary.meanDeployCadenceMinutes ?? null },
  ];
}

const ACCENT_BORDER = {
  coding: 'border-cyan-400',
  pickup: 'border-red-400',
  review: 'border-cyan-400',
  qa: 'border-amber-400',
  uat: 'border-orange-400',
  deploy: 'border-emerald-400',
};

function clipPathForIndex(index, total) {
  const c = CHEVRON_PX;
  if (total === 1) {
    return 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
  }
  if (index === 0) {
    return `polygon(0 0, calc(100% - ${c}px) 0, 100% 50%, calc(100% - ${c}px) 100%, 0 100%)`;
  }
  if (index === total - 1) {
    return `polygon(${c}px 0, 100% 0, 100% 100%, ${c}px 100%, 0 50%)`;
  }
  return `polygon(${c}px 0, calc(100% - ${c}px) 0, 100% 50%, calc(100% - ${c}px) 100%, ${c}px 100%, 0 50%)`;
}

/**
 * Horizontal chevron strip for mean cycle phases. Responsive: horizontal scroll on narrow viewports.
 *
 * @param {object} props
 * @param {{ key: string, label: string, minutes: number|null }[]} props.phases
 * @param {boolean} [props.embedded] — when true, omit outer card chrome (parent provides border)
 */
export default function CycleTimeChevronStrip({ phases, embedded = false }: any) {
  const [infoPhaseKey, setInfoPhaseKey] = useState<any>(null);
  const n = phases.length;

  const infoPhase = useMemo(
    () => (infoPhaseKey ? phases.find((p) => p.key === infoPhaseKey) : null),
    [infoPhaseKey, phases]
  );
  const infoLines = infoPhase ? getPhaseHelpLines(infoPhase.key) : [];

  if (n === 0) return null;

  const outerClass = embedded
    ? 'border-0 shadow-none rounded-none bg-transparent overflow-hidden'
    : 'rounded-xl border border-dark-border bg-dark-surface shadow-md overflow-hidden';

  return (
    <>
      <div className={outerClass} role="list" aria-label="Average cycle time by phase">
        {/* min-w lets segments stay readable; scroll on small screens */}
        <div className="flex min-w-0 overflow-x-auto">
          <div className="flex flex-1 min-w-[min(100%,520px)] sm:min-w-full">
            {phases.map((phase, index) => {
              const borderAccent = ACCENT_BORDER[phase.key] || 'border-gray-400';
              const phaseTooltip = `${phase.label} phase — ${formatPhaseDuration(phase.minutes)}`;
              return (
                <Tooltip key={phase.key} content={phaseTooltip}>
                  <div
                    role="listitem"
                    className={`relative flex-1 min-w-[5.75rem] -ml-[14px] ${index === 0 ? 'ml-0' : ''}`}
                    style={{
                      clipPath: clipPathForIndex(index, n),
                      zIndex: n - index,
                    }}
                  >
                    <div
                      className={[
                        'h-full border-t-4 bg-dark-base/90 pl-3 pr-2 sm:pl-4 sm:pr-3 py-4 flex flex-col items-center justify-center text-center min-h-[5.5rem]',
                        borderAccent,
                        index > 0 ? 'pl-5 sm:pl-6' : '',
                      ].join(' ')}
                    >
                      <div className="flex items-center justify-center gap-0.5 max-w-full">
                        <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-slate-400">
                          {phase.label}
                        </span>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-slate-500 hover:text-red-400 focus:outline-none focus-visible:ring-offset-dark-base"
                          aria-label={`${phase.label} phase: definition and limitations`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setInfoPhaseKey(phase.key);
                          }}
                        >
                          <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden />
                        </button>
                      </div>
                      <span className="mt-1 text-base sm:text-lg font-bold text-slate-100 tabular-nums">
                        {formatPhaseDuration(phase.minutes)}
                      </span>
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>

      <Modal
        isOpen={Boolean(infoPhaseKey && infoPhase)}
        onClose={() => setInfoPhaseKey(null)}
        title={infoPhase ? `${infoPhase.label} phase` : ''}
        icon={infoPhase ? <Info className="w-5 h-5 text-vivid-red" aria-hidden /> : null}
        contentClassName="max-w-lg"
      >
        {infoLines.length > 0 ? (
          <ul className="list-disc pl-5 space-y-2 text-sm text-slate-300">
            {infoLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">No additional details for this phase.</p>
        )}
      </Modal>
    </>
  );
}

/**
 * Loading skeleton matching chevron row shape.
 * @param {{ segmentCount?: number }} props
 */
export function CycleTimeChevronStripSkeleton({ segmentCount = 4, embedded = false }: any) {
  const phases = Array.from({ length: segmentCount }, (_, i) => ({
    key: `s-${i}`,
    label: '',
    minutes: null,
  }));
  const outerClass = embedded
    ? 'border-0 shadow-none rounded-none bg-transparent overflow-hidden animate-pulse'
    : 'rounded-xl border border-dark-border bg-dark-surface shadow-md overflow-hidden animate-pulse';
  return (
    <div className={outerClass} aria-hidden>
      <div className="flex min-w-0 overflow-x-auto">
        <div className="flex flex-1 min-w-[min(100%,520px)] sm:min-w-full">
          {phases.map((p, index) => (
            <div
              key={p.key}
              className={`relative flex-1 min-w-[5.75rem] -ml-[14px] ${index === 0 ? 'ml-0' : ''}`}
              style={{
                clipPath: clipPathForIndex(index, segmentCount),
                zIndex: segmentCount - index,
              }}
            >
              <div className="h-full border-t-4 border-dark-border bg-dark-elevated/80 py-4 min-h-[5.5rem] flex items-center justify-center">
                <div className="h-4 w-16 rounded bg-dark-elevated" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
